import {useEffect, useRef, useState} from 'react';
import {Plus, AlertCircle} from 'lucide-react';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FieldGroup,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@kroll-resolver-crisp/ui-components-react';
import {
  InvestigationFormFields,
  EMPTY_FORM_VALUE,
  isComplete,
  type InvestigationFormValue
} from './InvestigationFormFields';
import {type CreateJobRequest, type Job} from '@/api/jobs';
import {
  useCreateJob,
  useCreateImageJob,
  useCreateGoogleSearchJob,
  useCreatePlaylistJob,
  useCreateChannelJob,
  useCreatePinterestProfileJob,
  useCreatePinterestBoardJob,
  useSetJobFolder
} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {PLATFORM_ID} from '@/lib/platform';

type Props = {
  // Called once the job exists. Can fire twice for one job - once ungrouped if folder assignment
  // fails, then again once the assignment succeeds on retry - so callers should dedupe by id.
  readonly onCreated?: (job: Job) => void;
  /** Pre-assigns the created investigation to this folder instead of leaving it ungrouped. */
  readonly folderId?: string;
  /**
   'default' = labelled toolbar button; 'compact' = "+" icon for a folder row or rail header;
   'none' = no trigger at all, for a dialog opened from a menu item through `open`.
   */
  readonly trigger?: 'default' | 'compact' | 'none';
  readonly triggerLabel?: string;
  /**
   Controlled open state. The investigations rail opens one dialog from several menus (each folder's
   "New investigation" and the Unfiled one), so it owns the state and passes the folder along.
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /**
   What the form starts from: an investigation's Duplicate action passes that investigation's
   values. Read on mount only, so a caller changing it re-keys the dialog.
   */
  readonly initialValue?: InvestigationFormValue;
};

// Runs the video and channel search together. A lone failure still returns the job that was
// created, naming the kind to retry; both failing rethrows so the caller's generic error shows.
async function createVideoAndChannelJobs(
  create: (body: CreateJobRequest) => Promise<Job>,
  body: CreateJobRequest
): Promise<{jobs: [Job, ...Job[]]; missingKind?: 'video' | 'channel'}> {
  const [video, channel] = await Promise.allSettled([
    create(body),
    create({...body, search_type: 'channel'})
  ]);
  if (video.status === 'fulfilled' && channel.status === 'fulfilled') {
    return {jobs: [video.value, channel.value]};
  }

  if (video.status === 'fulfilled') {
    return {jobs: [video.value], missingKind: 'channel'};
  }

  if (channel.status === 'fulfilled') {
    return {jobs: [channel.value], missingKind: 'video'};
  }

  throw video.reason instanceof Error ? video.reason : new Error('Failed to create investigation');
}

export function NewInvestigationDialog({
  onCreated,
  folderId,
  trigger = 'default',
  triggerLabel = 'New investigation',
  open: controlledOpen,
  onOpenChange,
  initialValue = EMPTY_FORM_VALUE
}: Props) {
  const createJob = useCreateJob();
  const createImageJob = useCreateImageJob();
  const createGoogleSearchJob = useCreateGoogleSearchJob();
  const createPlaylistJob = useCreatePlaylistJob();
  const createChannelJob = useCreateChannelJob();
  const createPinterestProfileJob = useCreatePinterestProfileJob();
  const createPinterestBoardJob = useCreatePinterestBoardJob();
  const setJobFolder = useSetJobFolder();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = (next: boolean) => {
    setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const [value, setValue] = useState<InvestigationFormValue>(initialValue);
  const [error, setError] = useState<string | null>(null);
  // Holds the jobs across a retry of a failed folder assignment, so re-submitting doesn't create
  // duplicates. Cleared below whenever the analyst edits the form, so an edited resubmit always
  // creates fresh jobs instead of silently filing the originals under the new folder.
  const pendingJobsRef = useRef<Job[]>([]);
  // The kind of a Both search still to create, kept beside the jobs so a folder-assignment retry
  // doesn't forget it.
  const pendingMissingKindRef = useRef<'video' | 'channel' | undefined>(undefined);

  const clearPending = () => {
    pendingJobsRef.current = [];
    pendingMissingKindRef.current = undefined;
  };

  useEffect(clearPending, [value]);

  const isPending =
    createJob.isPending ||
    createImageJob.isPending ||
    createGoogleSearchJob.isPending ||
    createPlaylistJob.isPending ||
    createChannelJob.isPending ||
    createPinterestProfileJob.isPending ||
    createPinterestBoardJob.isPending ||
    setJobFolder.isPending;
  const canSubmit = isComplete(value);

  const resetForm = () => {
    setValue(EMPTY_FORM_VALUE);
  };

  const submit = async () => {
    if (!canSubmit) {
      return;
    }

    setError(null);

    try {
      let jobs: Job[];
      // Set when only one half of a Both search was created: the kind still to run.
      let missingKind: 'video' | 'channel' | undefined;
      if (pendingJobsRef.current.length > 0) {
        // Retrying a failed folder assignment: the jobs already exist, don't create more.
        jobs = pendingJobsRef.current;
        missingKind = pendingMissingKindRef.current;
      } else {
        let job: Job;
        let siblings: Job[] = [];
        switch (value.type) {
          case 'query': {
            const body: CreateJobRequest = {
              search_term: value.searchTerm,
              platform: PLATFORM_ID,
              target_policy_violations: value.targetPolicyViolations
            };
            if (value.intent) {
              body.intent = value.intent;
            }

            if (value.language) {
              body.language = value.language;
            }

            if (value.recency !== 'year') {
              body.recency = value.recency;
            }

            if (value.resultKind === 'both') {
              // Two jobs, one per strategy: each is one Scrapy message and stays rerunnable alone.
              const both = await createVideoAndChannelJobs(createJob.mutateAsync, body);
              missingKind = both.missingKind;
              [job, ...siblings] = both.jobs;
              break;
            }

            job = await createJob.mutateAsync(
              value.resultKind === 'channel' ? {...body, search_type: 'channel'} : body
            );
            break;
          }

          case 'image': {
            job = await createImageJob.mutateAsync({
              file: value.image!,
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {}),
              targetPolicyViolations: value.targetPolicyViolations
            });
            break;
          }

          case 'google': {
            job = await createGoogleSearchJob.mutateAsync({
              search_term: value.textValue,
              target_policy_violations: value.targetPolicyViolations,
              ...(value.recency === 'year' ? {} : {recency: value.recency}),
              ...(value.country ? {country: value.country} : {}),
              ...(value.extraEngines.length > 0
                ? {engines: ['google', ...value.extraEngines]}
                : {}),
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {})
            });
            break;
          }

          case 'playlist': {
            job = await createPlaylistJob.mutateAsync({
              playlist_url_or_id: value.textValue,
              target_policy_violations: value.targetPolicyViolations,
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {})
            });
            break;
          }

          case 'channel': {
            job = await createChannelJob.mutateAsync({
              channel_url_or_handle_or_id: value.textValue,
              target_policy_violations: value.targetPolicyViolations,
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {})
            });
            break;
          }

          case 'pinterest_profile': {
            job = await createPinterestProfileJob.mutateAsync({
              profile_url_or_username: value.textValue,
              target_policy_violations: value.targetPolicyViolations,
              follower_depth: value.collectFollowersOfFollowers
                ? 2
                : value.collectFollowers
                  ? 1
                  : 0,
              ...(value.collectPins ? {collect_pins: true} : {}),
              ...(value.collectBoards ? {collect_boards: true} : {}),
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {})
            });
            break;
          }

          case 'pinterest_search': {
            job = await createJob.mutateAsync({
              search_term: value.textValue,
              platform: PLATFORM_ID,
              search_type: value.searchKind,
              target_policy_violations: value.targetPolicyViolations,
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {})
            });
            break;
          }

          case 'pinterest_board': {
            job = await createPinterestBoardJob.mutateAsync({
              board_url_or_path: value.textValue,
              target_policy_violations: value.targetPolicyViolations,
              ...(value.intent ? {intent: value.intent} : {}),
              ...(value.language ? {language: value.language} : {})
            });
            break;
          }
        }

        jobs = [job, ...siblings];
        pendingJobsRef.current = jobs;
        pendingMissingKindRef.current = missingKind;
      }

      // Assign the folder before notifying the parent: onCreated seeds local state directly
      // from the object it's given, so a job handed over before it's placed shows up ungrouped
      // until a refresh.
      const placed: Job[] = [];
      for (const job of jobs) {
        if (!folderId) {
          placed.push(job);
          continue;
        }

        try {
          placed.push(await setJobFolder.mutateAsync({id: job.id, folderId}));
        } catch (error_: unknown) {
          // The job exists but is ungrouped: surface every created job (the placed ones too, which
          // the parent dedupes by id) so none is lost if the analyst closes the dialog, and keep
          // it open with the error so the analyst can retry the assignment without re-creating
          // anything.
          for (const created of [...placed, ...jobs.slice(placed.length)]) {
            onCreated?.(created);
          }

          const message = getApiErrorMessage(
            error_,
            'Created investigation, but failed to assign to folder'
          );
          setError(
            missingKind ? `${message}. The ${missingKind} search also failed to start.` : message
          );
          return;
        }
      }

      clearPending();
      for (const job of placed) {
        onCreated?.(job);
      }

      if (missingKind) {
        // Keep the dialog open on the half that failed, so a retry creates only that one.
        setValue({...value, resultKind: missingKind});
        setError(
          `Created one search, but the ${missingKind} search failed. Run it again to retry.`
        );
        return;
      }

      setOpen(false);
      resetForm();
    } catch (error_: unknown) {
      setError(getApiErrorMessage(error_, 'Failed to create investigation'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          // Closing after a failed folder assignment: the job already exists (just ungrouped),
          // so don't let a future submit try to create a second one. Also drop the stale error
          // and the filled-in form, so reopening the dialog starts clean rather than resubmitting
          // the same job's data over the assignment error.
          clearPending();
          setError(null);
          resetForm();
        }

        setOpen(next);
      }}>
      {trigger === 'none' ? null : trigger === 'compact' ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={triggerLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(true);
                }}
              />
            }>
            <Plus />
          </TooltipTrigger>
          <TooltipContent side="right">{triggerLabel}</TooltipContent>
        </Tooltip>
      ) : (
        <Button
          size="sm"
          onClick={() => {
            setOpen(true);
          }}>
          <Plus />
          {triggerLabel}
        </Button>
      )}

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Investigation</DialogTitle>
          <DialogDescription>
            {PLATFORM_ID === 'pinterest' ? 'Collect from Pinterest' : 'Search YouTube for content'}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        ) : null}
        <FieldGroup className="gap-4">
          <InvestigationFormFields
            value={value}
            onChange={setValue}
            onSubmit={() => void submit()}
          />
          <Button
            className="w-full"
            disabled={isPending || !canSubmit}
            onClick={() => void submit()}>
            {isPending ? (
              <>
                <Spinner />
                Starting…
              </>
            ) : (
              'Run Investigation'
            )}
          </Button>
        </FieldGroup>
      </DialogContent>
    </Dialog>
  );
}
